import {
  Component,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
  DestroyRef,
  input,
} from '@angular/core';
import {
  AppliedFilterType,
  FilterType,
} from '@app/core/interfaces/filter.interface';
import { EventBusService } from '@app/core/services/event-bus/event-bus.service';
import { Popover } from 'primeng/popover';
import { Ag1ListSearchComponent } from '@app/core/components/ag1-list-search/ag1-list-search.component';
import { take, tap } from 'rxjs/operators';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Ag1ListSearchComponent as Ag1ListSearchComponent_1 } from '../ag1-list-search/ag1-list-search.component';
import { Ag1FilterComponent } from './ag1-filter/ag1-filter.component';
import { Tooltip } from 'primeng/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ag1-filter-group',
  templateUrl: './ag1-filter-group.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    Bind,
    Button,
    AngularSvgIconModule,
    Popover,
    Ag1ListSearchComponent_1,
    Ag1FilterComponent,
    Tooltip,
  ],
})
export class Ag1FilterGroupComponent implements OnInit, OnDestroy {
  private _destroyRef = inject(DestroyRef);
  private eventBus = inject(EventBusService);

  readonly disabled = input(false);
  readonly disableFilters = input<string[]>([]);
  readonly name = input<string>(undefined);
  readonly showDateSelectionPreset = input<boolean>(false);

  @Output()
  filterEvent: EventEmitter<void> = new EventEmitter();

  showFiltersDropdown: boolean = false;
  filters: FilterType[] = [];
  filteredFilters: FilterType[] = [];
  appliedFilters: AppliedFilterType;
  pendingAppliedFilters: AppliedFilterType = {};
  showClearButton: boolean = false;
  isActive: boolean = false;

  @ViewChild('filterPopover')
  filterPopover!: Popover;

  @ViewChild(Ag1ListSearchComponent) searchComponent: Ag1ListSearchComponent;

  constructor() {}

  ngOnInit(): void {
    this.eventBus
      .getChannel('filters')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((filters) => {
        const list = Array.isArray(filters) ? filters : [];
        this.filters = list;
        this.filteredFilters = list;
      });
    this.eventBus
      .getChannel('appliedFilters')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((appliedFilters) => {
        // Clone to prevent mutations from affecting the original object
        this.appliedFilters = structuredClone(appliedFilters);
        this.showClearButton = !!Object.keys(appliedFilters).length;
      });
    this.eventBus
      .getChannel('pendingAppliedFilters')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((pendingAppliedFilters) => {
        // Clone to prevent mutations from affecting the original object
        this.pendingAppliedFilters = structuredClone(
          pendingAppliedFilters || {},
        );
      });
  }

  clearAppliedFilters(): void {
    // only remove applied filters that aren't locked
    const appliedFilters = Object.keys(this.appliedFilters).reduce(
      (obj, key) => {
        if (
          Object.prototype.hasOwnProperty.call(
            this.appliedFilters[key],
            'locked',
          ) &&
          this.appliedFilters[key]['locked']
        ) {
          obj[key] = this.appliedFilters[key];
        }
        return obj;
      },
      {},
    );

    this.eventBus.setChannel('pendingAppliedFilters', appliedFilters);
    this.eventBus.setChannel('appliedFilters', appliedFilters);
    if (!Object.keys(appliedFilters).length) {
      this.showClearButton = false;
    }
    // Apply the cleared filters
    this.filterEvent.emit();
  }

  filterRequirementsUnmet(): boolean {
    if (!Array.isArray(this.filters) || this.filters?.length === 0) {
      // only check requirements if filters are present
      return false;
    }

    // Check pendingAppliedFilters instead of appliedFilters since that's what the user is currently editing
    const filtersToCheck = this.pendingAppliedFilters || {};

    const requiredUnsetFilters = this.filters?.filter((f) => {
      if (!f.required) {
        return false;
      }

      const filterValue = filtersToCheck[f.name];

      // Check if filter is null or undefined
      if (filterValue == null || filterValue === undefined) {
        return true;
      }

      // For dropdown types (arrays), check if array is empty
      if (Array.isArray(filterValue)) {
        return filterValue.length === 0;
      }

      // For object types (dateRange, week, text, number), check if object is empty
      if (typeof filterValue === 'object') {
        return Object.keys(filterValue).length === 0;
      }

      return false;
    });

    return requiredUnsetFilters.length > 0;
  }

  ngOnDestroy(): void {
    this.eventBus.setChannel('pendingAppliedFilters', {});
  }

  onMenuOpen(): void {
    this.isActive = true;
    // Initialize pendingAppliedFilters with a clone of current appliedFilters
    // This ensures mutations in filter components don't affect the original
    this.eventBus.setChannel(
      'pendingAppliedFilters',
      structuredClone(this.appliedFilters),
    );
    setTimeout(() => {
      this.searchComponent?.focusSearch();
    });
  }

  onMenuClose(): void {
    this.isActive = false;
    // Get the latest pendingAppliedFilters and ensure it's cloned
    const pendingFilters = this.eventBus.getChannelValue(
      'pendingAppliedFilters',
    );
    if (pendingFilters) {
      this.eventBus.setChannel(
        'pendingAppliedFilters',
        structuredClone(pendingFilters),
      );
    }
    this.resetFilteredFilters();
  }

  resetFilteredFilters(): void {
    this.filteredFilters = Array.isArray(this.filters) ? [...this.filters] : [];
  }

  closeFilters(): void {
    this.filterPopover?.hide();
  }

  applyFilters(): void {
    this.closeFilters();
    this.eventBus
      .getChannel('pendingAppliedFilters')
      .pipe(
        take(1),
        tap((pendingAppliedFilters) => {
          // Clone before committing to ensure immutability
          const clonedFilters = structuredClone(pendingAppliedFilters);
          this.appliedFilters = clonedFilters;
          this.eventBus.setChannel('appliedFilters', clonedFilters);
          this.filterEvent.emit();
        }),
      )
      .subscribe();
  }
}
